/**
 * Kerandy — Motion & Interactivity engine
 *
 * Pairs with `assets/kerandy-motion.css` and is configured by
 * `snippets/kerandy-motion.liquid`, which writes `window.KerandyMotion` from
 * theme settings and adds the `kd-motion` class to <html> before first paint.
 *
 * This module owns the *behaviour*; the stylesheet owns the *appearance*.
 * The one thing they share is the reveal candidate list, which is duplicated
 * in REVEAL_SELECTOR below — keep the two in sync when adding a section.
 *
 * Failure behaviour is deliberate. The bootstrap arms a failsafe that strips
 * `kd-motion`, and this module cancels it on boot; if boot throws, we strip the
 * class ourselves. Either way a broken or blocked script leaves the storefront
 * exactly as it was rather than leaving content invisible.
 */

const config = Object.assign(
  {
    reveal: true,
    parallax: true,
    magnetic: true,
    header: true,
    progress: true,
    staggerStep: 70,
    staggerCap: 420,
  },
  window.KerandyMotion || {}
);

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

/* -------------------------------------------------------------------------
 * 1. Scroll reveal
 * ---------------------------------------------------------------------- */

/** Mirror of the candidate list in kerandy-motion.css. */
const REVEAL_SELECTOR = [
  '[data-kd-reveal]',
  '.kd-chead > *',
  '.kd-brand > *',
  '.kd-quote > *',
  '.kd-style-grid > *',
  '.kd-product-grid > *',
  '.kd-results-grid > *',
  '.kd-routine-grid > *',
  '.kd-paths__grid > *',
  '.kd-solutions__grid > *',
  '.kd-education-grid > *',
  '.kd-guide__grid > *',
  '.kd-guide__steps > *',
  '.kd-gallery__grid > *',
  '.kd-fulfill__grid > *',
  '.kd-visit__grid > *',
  '.kd-contact__grid > *',
  '.kd-reviews__grid > *',
  '.kd-review-grid > *',
  '.kd-bundle-grid > *',
  '.kd-cat-grid > *',
  '.kd-svc-grid > *',
  '.kd-oil-guide__grid > *',
  '.kd-bc__grid > *',
  '.kd-bc__steps > *',
  '.kd-bc__guides > *',
  '.kd-bc__kit-grid > *',
  '.kd-look-products > *',
  '.kd-trust-row > *',
  '.kd-consult__hero-grid > div > *',
  // :not(.product-card *) matters: Horizon builds card titles and prices out of
  // .text-block too, and revealing those separately from the card that contains
  // them double-animates the same pixels and strands the price if either half
  // misses. The card is the unit that reveals.
  '#MainContent > .shopify-section .text-block:not(.product-card *)',
  '#MainContent > .shopify-section .group-block:not(.group-block *):not(.product-card *)',
  '#MainContent .product-card',
].join(',');

/** Subtrees the reveal engine must not touch — functional UI, not decoration. */
const REVEAL_EXCLUDE =
  '[data-kd-no-motion],.kd-bk-overlay,.kd-bkpg,.kd-bk-cal-grid,dialog,[popover],.cart-drawer,.facets';

/**
 * Registered but not yet revealed.
 *
 * Deliberately NOT a WeakSet keyed on node identity. Horizon re-renders through
 * a morph that REUSES the existing elements and rewrites their attributes, so a
 * revealed product card comes back with `data-kd-state` stripped but the very
 * same node reference. Identity-based bookkeeping treats it as already handled
 * and it stays invisible forever — which is exactly what happened to every card
 * after a collection filter. Live state is the only trustworthy source.
 */
const pending = new Set();
let revealObserver = null;

function show(element, delay) {
  element.style.setProperty('--kd-anim-delay', `${delay}ms`);
  element.setAttribute('data-kd-state', 'shown');
  pending.delete(element);
  if (revealObserver) revealObserver.unobserve(element);
}

/**
 * Reveal delays are assigned per parent so a grid of cards cascades, while two
 * unrelated blocks that happen to scroll in together both start at zero.
 */
function staggerDelay(element) {
  const parent = element.parentElement;
  if (!parent) return 0;

  const index = Array.prototype.indexOf.call(parent.children, element);
  return Math.min(Math.max(index, 0) * config.staggerStep, config.staggerCap);
}

function registerReveal(element) {
  if (element.getAttribute('data-kd-state') === 'shown') return;
  if (pending.has(element)) return;
  if (element.closest(REVEAL_EXCLUDE)) return;

  pending.add(element);
  // Re-observing an element already under observation is a no-op, so this stays
  // safe even when a rescan sees the same node twice.
  revealObserver.observe(element);
}

function scanReveal(root = document) {
  if (!config.reveal || !revealObserver) return;

  const scope = root.nodeType === Node.ELEMENT_NODE ? root : document;

  if (scope !== document && scope.matches && scope.matches(REVEAL_SELECTOR)) {
    registerReveal(scope);
  }

  scope.querySelectorAll(REVEAL_SELECTOR).forEach(registerReveal);

  // Sections get torn out and re-rendered constantly. Drop the detached nodes
  // so `pending` cannot grow into a leak over a long browsing session.
  for (const element of pending) {
    if (!element.isConnected) pending.delete(element);
  }
}

/**
 * Reveal whatever is already on screen, without waiting for the observer.
 *
 * This is the page-load choreography, and it is also what makes the layer
 * correct in a background tab: IntersectionObserver callbacks do not run while
 * a document is not being rendered, so a link opened in a new tab would
 * otherwise sit at opacity 0 until the visitor switched to it.
 */
function revealVisible() {
  let index = 0;

  for (const element of [...pending]) {
    const rect = element.getBoundingClientRect();
    if (rect.top >= window.innerHeight || rect.bottom <= 0) continue;
    show(element, Math.min(index * config.staggerStep, config.staggerCap));
    index += 1;
  }
}

function initReveal() {
  if (!config.reveal || !('IntersectionObserver' in window)) return;

  revealObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        show(entry.target, staggerDelay(entry.target));
      }
    },
    {
      // Hold the reveal until the element is meaningfully on screen, but never
      // so late that a short section reveals below the fold.
      rootMargin: '0px 0px -10% 0px',
      threshold: 0,
    }
  );

  scanReveal(document);
  revealVisible();

  // A tab opened in the background renders nothing, so the observer stays
  // silent. Catch up the moment it comes to the front.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') revealVisible();
  });

  // Sections re-render constantly here: facet filtering, quick-add, cart
  // updates, and every change in the theme editor. Watching the tree means new
  // markup is registered rather than left permanently at opacity 0.
  //
  // It watches <body> rather than #MainContent so the observer survives any
  // wholesale replacement of the main element; <body> is never swapped out.
  //
  // The callback rescans the whole document rather than just the added nodes.
  // A morph that reuses an element and only rewrites its attributes produces no
  // childList record for that element, so walking `addedNodes` alone misses
  // precisely the cards whose `data-kd-state` was just wiped. A debounced full
  // rescan is cheap and catches both cases.
  new MutationObserver(() => scheduleRescan()).observe(document.body, {
    childList: true,
    subtree: true,
  });
}

/* Long enough that a chatty third-party script cannot force a rescan every
   tick, short enough to be imperceptible after a filter or cart update. */
const RESCAN_DEBOUNCE_MS = 100;
let rescanPending = false;

/**
 * Coalesce the burst of mutations a section re-render produces into one rescan.
 *
 * setTimeout rather than requestAnimationFrame: rAF is parked in a background
 * tab, and this is precisely the path that must still resolve there.
 */
function scheduleRescan() {
  if (rescanPending) return;
  rescanPending = true;

  window.setTimeout(() => {
    rescanPending = false;
    try {
      scanReveal(document);
      // Re-rendered content is usually already on screen, and the observer can
      // be slow to deliver it. Show what is visible instead of betting on it.
      revealVisible();
    } catch {
      // A rescan must never be able to take down the whole engine.
    }
  }, RESCAN_DEBOUNCE_MS);
}

/* -------------------------------------------------------------------------
 * 2. Hero pan
 * ---------------------------------------------------------------------- */

const PAN_SELECTOR = '.kd-consult__hero-grid > img, .kd-hero__img, .kd-brand img, .kd-look > img';
const PAN_AMPLITUDE = 6; // percentage points of object-position travel
const panTargets = [];

function initParallax() {
  if (!config.parallax || prefersReducedMotion.matches) return;

  document.querySelectorAll(PAN_SELECTOR).forEach((image) => {
    // Read the authored crop before overriding it, so each image keeps the
    // framing the design already chose (hero sits at 25%, gallery at top).
    const authored = getComputedStyle(image).objectPosition.split(' ')[1] || '50%';
    const base = parseFloat(authored);
    if (Number.isNaN(base)) return;

    image.style.setProperty('--kd-pan-base', `${base}%`);
    image.setAttribute('data-kd-pan', '');
    panTargets.push({ image, base });
  });

  if (panTargets.length) scheduleScrollWork();
}

function updatePan() {
  const viewportHeight = window.innerHeight;

  for (const { image, base } of panTargets) {
    const rect = image.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > viewportHeight) continue;

    // -1 when the element sits below the fold, +1 when it has scrolled past.
    const elementCentre = rect.top + rect.height / 2;
    const viewportCentre = viewportHeight / 2;
    const span = viewportHeight / 2 + rect.height / 2;
    const progress = Math.max(-1, Math.min(1, (viewportCentre - elementCentre) / span));

    // Clamp against the real 0–100% range so the crop can never run off frame.
    const target = Math.max(0, Math.min(100, base + progress * PAN_AMPLITUDE));
    image.style.setProperty('--kd-pan', `${(target - base).toFixed(2)}%`);
  }
}

/* -------------------------------------------------------------------------
 * 3. Magnetic buttons
 * ---------------------------------------------------------------------- */

const MAGNET_SELECTOR = '.kd-cbtn, .kd-btn';
const MAGNET_STRENGTH = 0.22;
const MAGNET_MAX = 7; // px
let activeMagnet = null;

function releaseMagnet() {
  if (!activeMagnet) return;
  activeMagnet.style.removeProperty('--kd-mag-x');
  activeMagnet.style.removeProperty('--kd-mag-y');
  activeMagnet.removeAttribute('data-kd-magnetic');
  activeMagnet = null;
}

function initMagnetic() {
  if (!config.magnetic || !finePointer.matches || prefersReducedMotion.matches) return;

  // One delegated listener rather than per-button binding, so buttons added by
  // quick-add, the cart drawer or a section re-render work with no rebinding.
  document.addEventListener(
    'pointermove',
    (event) => {
      const button = event.target instanceof Element ? event.target.closest(MAGNET_SELECTOR) : null;

      if (!button) {
        releaseMagnet();
        return;
      }

      if (button !== activeMagnet) {
        releaseMagnet();
        activeMagnet = button;
        button.setAttribute('data-kd-magnetic', 'active');
      }

      const rect = button.getBoundingClientRect();
      const dx = event.clientX - (rect.left + rect.width / 2);
      const dy = event.clientY - (rect.top + rect.height / 2);

      const clamp = (value) => Math.max(-MAGNET_MAX, Math.min(MAGNET_MAX, value * MAGNET_STRENGTH));
      button.style.setProperty('--kd-mag-x', `${clamp(dx).toFixed(2)}px`);
      button.style.setProperty('--kd-mag-y', `${clamp(dy).toFixed(2)}px`);
    },
    { passive: true }
  );

  document.addEventListener('pointerleave', releaseMagnet);
  window.addEventListener('blur', releaseMagnet);
}

/* -------------------------------------------------------------------------
 * 4. Header state and reading progress
 * ---------------------------------------------------------------------- */

let progressBar = null;
let cachedWrapper = null;

/**
 * Return the current scroll offset and maximum, whoever is doing the scrolling.
 *
 * This theme does NOT scroll the document: `html` and `body` are
 * `overflow: hidden` and `.page-wrapper` is the real scroll container, so
 * `window.scrollY` reads a permanent 0. Resolving it per call rather than once
 * at boot also covers the drawers and modals that move the scroll lock around.
 */
function readScroll() {
  const doc = document.scrollingElement || document.documentElement;
  if (doc.scrollHeight > doc.clientHeight + 4) {
    return { top: doc.scrollTop, max: doc.scrollHeight - doc.clientHeight };
  }

  if (!cachedWrapper || !cachedWrapper.isConnected) {
    cachedWrapper = document.querySelector('.page-wrapper');
  }

  if (cachedWrapper && cachedWrapper.scrollHeight > cachedWrapper.clientHeight + 4) {
    return {
      top: cachedWrapper.scrollTop,
      max: cachedWrapper.scrollHeight - cachedWrapper.clientHeight,
    };
  }

  return { top: 0, max: 0 };
}

function initProgress() {
  if (!config.progress || prefersReducedMotion.matches) return;
  progressBar = document.createElement('div');
  progressBar.className = 'kd-scroll-progress';
  progressBar.setAttribute('aria-hidden', 'true');
  document.body.appendChild(progressBar);
}

function updateChrome() {
  const { top, max } = readScroll();

  if (config.header) {
    document.documentElement.toggleAttribute('data-kd-scrolled', top > 8);
  }

  if (progressBar) {
    const progress = max > 0 ? Math.min(1, top / max) : 0;
    progressBar.style.setProperty('--kd-progress', progress.toFixed(4));
  }
}

/* -------------------------------------------------------------------------
 * 6. Opt-in number count-up — <span data-kd-count="120">120</span>
 * ---------------------------------------------------------------------- */

function initCounters() {
  const counters = document.querySelectorAll('[data-kd-count]');
  if (!counters.length || !('IntersectionObserver' in window)) return;

  if (prefersReducedMotion.matches) return;

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        observer.unobserve(entry.target);

        const element = entry.target;
        const target = parseFloat(element.getAttribute('data-kd-count'));
        if (Number.isNaN(target)) continue;

        const duration = 1100;
        const start = performance.now();

        const step = (now) => {
          const t = Math.min(1, (now - start) / duration);
          const eased = 1 - Math.pow(1 - t, 3);
          element.textContent = Math.round(target * eased).toLocaleString();
          if (t < 1) requestAnimationFrame(step);
        };

        requestAnimationFrame(step);
      }
    },
    { threshold: 0.4 }
  );

  counters.forEach((counter) => observer.observe(counter));
}

/* -------------------------------------------------------------------------
 * 7. Shared scroll loop
 * ---------------------------------------------------------------------- */

let ticking = false;

function scheduleScrollWork() {
  if (ticking) return;
  ticking = true;
  requestAnimationFrame(() => {
    updateChrome();
    if (panTargets.length) updatePan();
    ticking = false;
  });
}

/* -------------------------------------------------------------------------
 * 8. Boot
 * ---------------------------------------------------------------------- */

/** Give up cleanly: strip the class so every hidden rule stops applying. */
function abandon(reason) {
  document.documentElement.classList.remove('kd-motion');
  console.warn('[kerandy-motion] disabled —', reason);
}

function boot() {
  // Cancel the bootstrap failsafe by hand rather than letting it race us on a
  // timer. A throttled tab can delay this module well past any deadline, and
  // losing that race used to silently disable the whole layer.
  if (window.__kdMotion && typeof window.__kdMotion.cancel === 'function') {
    window.__kdMotion.cancel();
  }

  try {
    initReveal();
    initParallax();
    initMagnetic();
    initProgress();
    initCounters();
  } catch (error) {
    // Motion is never worth a blank page.
    abandon(error);
    return;
  }

  // Capture phase on the document, not a `window` scroll listener: scroll
  // events do not bubble, and the element actually scrolling here is
  // `.page-wrapper` rather than the document. Capture catches it either way,
  // including inside drawers and any future scroll container.
  document.addEventListener('scroll', scheduleScrollWork, { passive: true, capture: true });
  window.addEventListener('resize', scheduleScrollWork, { passive: true });
  scheduleScrollWork();

  document.documentElement.classList.add('kd-motion-ready');

  // Theme editor: a re-rendered section arrives with fresh, still-hidden
  // markup, and its reveal candidates need registering again.
  document.addEventListener('shopify:section:load', scheduleRescan);

  // If the visitor turns reduced motion on mid-session, drop everything.
  prefersReducedMotion.addEventListener('change', (event) => {
    if (!event.matches) return;
    releaseMagnet();
    document.querySelectorAll('[data-kd-state]').forEach((element) => {
      element.setAttribute('data-kd-state', 'shown');
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
